import type { OptionsDocument, PresentationOptionV2, QuoteDocument } from 'paintscout'
import { createPresentationNextOption } from '../../../options/presentation/create-presentation-next-option'
import { isPresentationV2Doc } from '../is-presentation-next'
import { getQuotePresentation } from './get-quote-presentation'

/**
 * Resolves a quote's presentation for the **v2** ("PresentationNext") renderer, guaranteeing a
 * v2-shaped document. The mirror of getQuoteV1Presentation.
 *
 * The v2 renderer reads `pages[].sections[].blocks[]`; a v1 document has no `blocks`, so it would
 * render as a set of empty sections. Callers that branch on version (customer-view Index,
 * QuotePreviewDialog — #21310) never get here with the wrong shape, so anything that does is a
 * routing bug: it degrades to an empty v2 presentation and logs, rather than rendering a
 * confusingly blank page with no explanation.
 *
 * Note there is no "default v2 presentation" to fall back to the way v1 has one — v2 has no
 * equivalent of getDefaultPresentation — hence the empty document.
 */
export function getQuoteNextPresentation(args: {
  options: OptionsDocument
  quote: QuoteDocument
  inactive?: boolean
  isWorkOrder?: boolean
  isProductOrderForm?: boolean
  current?: boolean
}): PresentationOptionV2 {
  const presentation = getQuotePresentation(args)

  if (isPresentationV2Doc(presentation)) {
    return presentation
  }

  console.warn(
    `getQuoteNextPresentation: presentation "${presentation?.key}" is not v2-shaped but reached the ` +
      `v2 renderer; falling back to an empty presentation`
  )

  return createPresentationNextOption({ options: args.options, label: presentation?.label ?? 'Presentation' })
}
